import "./style.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { FaCalendarAlt, FaClock } from "react-icons/fa";
import Placeholder from '../../../assets/images/placeholder.jpg';
import { PulseLoader } from "react-spinners";
import { usePageTitle } from "../../../Hooks/usePageTitle";
import {
  getAffiliateNotifications,
  markAllAffiliateNotificationsAsRead,
  markAffiliateNotificationAsRead,
  markAffiliateNotificationAsUnread,
} from "../../../Services/Affiliate/Notifications";
import { notificationOptions } from "../../../Utils/Constants/SelectOptions";
import { calculateTimePassed } from "../../../Utils/timeUtils";
import { formatDate, showErrorToast } from "../../../Utils/Utils";
import { showToast } from '../../../Components/Toast/toast';
import CustomSelect from "../../../Components/CustomSelect";

const NotificationsAffiliate = () => {
  usePageTitle("Notifications");
  const queryClient = useQueryClient();
  const [params, setParams] = useState({ per_page: 10 });

  // GET NOTIFICATIONS
  const {
    data: notificationsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["getAffiliateNotifications", params],
    queryFn: () => getAffiliateNotifications(params),
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const notifications = notificationsData?.data || [];

  if (isError) {
    console.error("Error Fetching Notifications:", error);
    showErrorToast(error);
  }

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllAffiliateNotificationsAsRead,
    onSuccess: () => {
      showToast("All notifications marked as read", "success");
      queryClient.invalidateQueries(["getAffiliateNotifications"]);
      queryClient.invalidateQueries(["getAffiliateUnreadNotifications"]);
      queryClient.invalidateQueries(["getAffiliateUnreadCount"]);
    },
    onError: (error) => {
      showToast(error.message || "Failed to mark all as read", "error");
    },
  });

  // Mark individual as read/unread mutation
  const toggleReadStatusMutation = useMutation({
    mutationFn: ({ uuid, isRead }) => {
      return isRead
        ? markAffiliateNotificationAsUnread(uuid)
        : markAffiliateNotificationAsRead(uuid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["getAffiliateNotifications"]);
      queryClient.invalidateQueries(["getAffiliateUnreadNotifications"]);
      queryClient.invalidateQueries(["getAffiliateUnreadCount"]);
    },
    onError: (error) => {
      showToast(error.message || "Failed to update notification status", "error");
    },
  });

  // handle Mark as Read / Unread toggle
  const toggleReadStatus = (notification) => {
    const uuid = notification.uuid || notification.id;
    const isRead = !!notification.read_at;
    toggleReadStatusMutation.mutate({ uuid, isRead });
  };

  // handle Filter Change
  const handleFilterChange = (selectedOption) => {
    const value = selectedOption.target.value;
    if (value === '') {
      // All notifications
      setParams((prev) => {
        const newParams = { ...prev };
        delete newParams.unread_only;
        delete newParams.status;
        return newParams;
      });
    } else if (value === 'unread') {
      // Unread only
      setParams((prev) => {
        const newParams = { ...prev };
        delete newParams.status;
        return { ...newParams, unread_only: 1 };
      });
    } else if (value === 'read') {
      // Read only
      setParams((prev) => {
        const newParams = { ...prev };
        delete newParams.unread_only;
        return { ...newParams, status: 'read' };
      });
    }
  };

  // handle Mark All as Read
  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  return (
    <section className="notification-page">
      <div className="admin-content-header mb-4 d-flex gap-2">
        <h2 className="screen-title mb-0">Notifications</h2>
      </div>
      <div className="admin-content-body rounded-20 p-4 p-lg-4 p-xxl-4 mb-4">
        <Row>
          <Col
            xs={12}
            className="notification-filter d-flex flex-column flex-sm-row justify-content-sm-between align-items-sm-center gap-3"
          >
            <div className="d-flex align-items-center gap-3">
              <CustomSelect
                label="Showing"
                labelClassName="mb-0"
                name="status"
                options={notificationOptions}
                firstIsLabel={false}
                className="gray"
                onChange={handleFilterChange}
                value={params.status || ''}
              />
            </div>
            <div>
              <Button
                className="p-2 btn btn-primary"
                variant="link"
                onClick={handleMarkAllAsRead}
                disabled={markAllAsReadMutation.isPending}
              >
                {markAllAsReadMutation.isPending ? "Processing..." : "Mark All as Read"}
              </Button>
            </div>
          </Col>
        </Row>

        <Row className="mt-4">
          {isLoading ? (
            <Col xs={12} className="text-center py-5">
              <PulseLoader />
            </Col>
          ) : notifications.length === 0 ? (
            <Col xs={12} className="text-center py-5">
              <p className="text-muted">No notifications found</p>
            </Col>
          ) : (
            notifications.map((notification) => (
              <Col xs={12} key={notification.uuid || notification.id} className="mb-3">
                <Card
                  className={`card-notifications flex-row px-3 px-md-4 py-3 py-md-4 gap-lg-2 gap-xxl-3 ${
                    !notification.read_at ? "unread" : ""
                  }`}
                >
                  <i className="noti-icon flex-shrink-0 align-self-center me-3 me-md-0">
                    <img 
                      src={notification.data?.img || notification.image || Placeholder} 
                      alt="Notification" 
                    />
                  </i>
                  <Card.Body className="p-0 d-md-flex align-self-center">
                    <div className="flex-grow-1 pe-0 px-md-3">
                      <Card.Text>{notification.data?.body || notification.body || notification.message}</Card.Text>
                      {notification.created_at && (
                        <div className="meta">
                          <time
                            className="time-meta"
                            dateTime={notification.created_at}
                          >
                            <FaCalendarAlt />
                            <span className="d-none">Date:</span>{" "}
                            {calculateTimePassed(notification.created_at)}
                          </time>
                          <time
                            className="time-meta"
                            dateTime={notification.created_at}
                          >
                            <FaClock />
                            <span className="d-none">Time:</span>{" "}
                            {formatDate(notification.created_at, "hh:mm A")}
                          </time>
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0 text-end d-flex align-items-center justify-content-end">
                      <Button
                        onClick={() => toggleReadStatus(notification)}
                        variant={`link fw-medium ${
                          notification.read_at ? "grayColor" : "blueColor"
                        }`}
                        disabled={toggleReadStatusMutation.isPending}
                      >
                        {notification.read_at ? "Mark as Unread" : "Mark as Read"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>

        {notificationsData?.links?.next && (
          <Row>
            <Col xs={12}>
              <div className="beechMein mt-4">
                <Button
                  className="btn btn-primary"
                  onClick={() => {
                    // Handle pagination if needed
                    refetch();
                  }}
                >
                  Load More
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </div>
    </section>
  );
};

export default NotificationsAffiliate;
